/**
 * ViewOnlyBadge Component
 * Shows "للعرض فقط" (View Only) badge for executive (Decision Maker) users
 */
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

import { tokens } from '@design';
// Eye icon for view-only indicator
const EyeIcon = ({ size = 14, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

interface ViewOnlyBadgeProps {
  /** Custom label text (default: uses t('common.viewOnly')) */
  label?: string;
  /** Badge size variant */
  size?: 'small' | 'medium' | 'large';
  /** Custom style */
  style?: object;
}

const ViewOnlyBadge: React.FC<ViewOnlyBadgeProps> = ({
  label,
  size = 'medium',
  style,
}) => {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();

  const displayLabel = label ?? t('common.viewOnly');

  // Theme colors for dark mode
  const themeColors = {
    background: isDark ? colors.backgroundAlt : tokens.colors.raw.gray["100"],
    text: colors.textMuted,
    icon: colors.textMuted,
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          container: { paddingHorizontal: tokens.spacing.absolute["2"], paddingVertical: tokens.spacing.absolute["1"], gap: tokens.spacing.absolute["1"] },
          text: { fontSize: 10 },
          iconSize: 12,
        };
      case 'large':
        return {
          container: { paddingHorizontal: tokens.spacing.absolute["4"], paddingVertical: tokens.spacing.absolute["2"], gap: tokens.spacing.absolute["2"] },
          text: { fontSize: tokens.typography.paragraph["paragraph small font size"] },
          iconSize: 18,
        };
      default: // medium
        return {
          container: { paddingHorizontal: tokens.spacing.absolute["3"], paddingVertical: tokens.spacing.absolute["1_5"], gap: tokens.spacing.absolute["1_5"] },
          text: { fontSize: tokens.typography.paragraph["paragraph mini font size"] },
          iconSize: 14,
        };
    }
  };

  const sizeStyles = getSizeStyles();

  return (
    <View style={[styles.container, sizeStyles.container, { backgroundColor: themeColors.background }, style]}>
      <EyeIcon size={sizeStyles.iconSize} color={themeColors.icon} />
      <Text style={[styles.text, sizeStyles.text, { color: themeColors.text }]}>{displayLabel}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.gray["100"],
    borderRadius: tokens.radii.absolute["radius-16"],
  },
  text: {
    color: tokens.colors.raw.gray["500"],
    fontFamily: FONTS.medium,
  },
});

export default ViewOnlyBadge;
